// Single source of truth for pricing. Home PricingPreview and the full
// /pricing page both consume this — divergent plan copy between the two
// surfaces makes the product look confused to returning visitors.

export type PlanId = "free" | "pro" | "max";

export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  featured: boolean;
}

// PACKAGING NOTE (2026-07-01, founder-led rebrand): the SOLD lineup is
// Free Tracker + the two study passes below, plus one deliberately quiet
// monthly option at $39 (internally the "max" tier -- checkout target
// "max", price set by STRIPE_MONTHLY_PRICE_ID). Pro and Max are NO
// LONGER SOLD as marketing tiers. Their old $39/$59 card copy used to
// live here "just in case" -- removed 2026-07-19 so the retired prices
// can never leak into the shipped bundle or a future refactor. The
// "pro"/"max" PlanId values stay: they are live internal tier ids
// (grandfathered subscribers, plan-gating, ctaLabelForPlan).
export const PLANS: Plan[] = [
  {
    id: "free",
    name: "Free",
    tagline: "Record your mistakes",
    price: "$0",
    period: "forever",
    description:
      "A structured place to record every question you miss. Unlimited, no card, and the data stays yours.",
    // Record-only free tier (2026-07 restructure). Accounts created
    // before the transition keep their promised analytics + planner
    // permanently (plans.py grandfathering) -- this list describes what
    // NEW accounts get, and deliberately under-promises until the
    // MAREN_NEW_FREE_TIER flag flips.
    features: [
      "Unlimited manual question logging",
      "Your own reflections on every miss",
      "Full question history: view, edit, delete",
      "Study-session timer and test-day countdown",
      "Full-length score logging",
      "Export everything, anytime. Your data stays yours forever",
    ],
    featured: false,
  },
];

// Auth-aware CTA. Logged-out visitors on /pricing shouldn't see
// "Upgrade to Pro" — they have nothing to upgrade from.
export function ctaLabelForPlan(planId: PlanId, authed: boolean, currentPlan: string | null): string {
  if (planId === "free") return authed ? "Your current plan" : "Start free";
  if (planId === currentPlan) return "Your current plan";
  if (!authed) return "Get full access";
  // An existing paid subscriber switching tiers manages it through the
  // Stripe Billing Portal (proration, no second subscription), so the
  // action is a "change", not a fresh "upgrade".
  if (currentPlan === "pro" || currentPlan === "max") return "Change plan";
  return "Get full access";
}

// Featured-tier pill. Same label on home and /pricing so returning
// visitors don't see contradictory framing.
//
// Earlier value: "Most students pick this." That's a quantitative claim
// that requires data backing (FTC §5 deception doctrine) — true of a
// large mature user base, deceptive while we're in open beta with a
// small N. "Recommended" carries the same UX signal (anchor visitors
// to the middle tier) without the substantiation burden. See
// legal-review-20260422.md §H2.
export const FEATURED_BADGE = "Recommended";

// ── Study passes (one-time, time-boxed access) ─────────────────────────
//
// Sold by prep window rather than by month: a pass grants the full (Max)
// feature set for its length, paid once. The dollar amount and any
// founding-cohort discount live in Stripe (one-time price + promotion
// codes); the ids map to backend PASS_CONFIG. A "checkout target" is any
// purchasable plan id OR pass id -- both flow through
// POST /billing/create-checkout-session.
//
// Prices and copy here are DRAFT pending the wording review
// (docs/landing-copy-review.md).
export type PassId = "pass_3mo" | "pass_6mo";
export type CheckoutTarget = PlanId | PassId;

export interface StudyPass {
  id: PassId;
  name: string;
  price: string; // one-time
  perMonth: string; // equivalent monthly, shown for value framing
  months: number;
  description: string;
  featured: boolean;
}

export const STUDY_PASSES: StudyPass[] = [
  {
    id: "pass_3mo",
    name: "3-Month Study Pass",
    price: "$99",
    perMonth: "about $33 a month",
    months: 3,
    description:
      "Full access for an active prep window. Every Maren AI feature, paid once, no monthly rebill.",
    featured: false,
  },
  {
    id: "pass_6mo",
    name: "6-Month Study Pass",
    price: "$149",
    perMonth: "about $25 a month",
    months: 6,
    description:
      "The best-value window for retakers, nontrads, and students starting early.",
    featured: true,
  },
];
